import { graphql, useStaticQuery } from 'gatsby';
import { getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { useMemo } from 'react';

interface UseOptimizedImageResult {
    rawImageData: IGatsbyImageData | null; // Raw gatsby image data for getImage()
    gatsbyImageData: IGatsbyImageData | null; // Processed for direct use
    altText: string;
}

export type ImageNode = {
    id: string;
    childImageSharp?: {
        gatsbyImageDataConstrained: IGatsbyImageData;
        gatsbyImageDataFullWidth: IGatsbyImageData;
    };
};

export type AllFileNodes = {
    nodes: Array<ImageNode>;
};

export type AllFileQuery = {
    allFile: AllFileNodes;
};

export type ImageLayout = 'CONSTRAINED' | 'FULL_WIDTH';

// Since we can't use dynamic queries with useStaticQuery, we'll get all files
// and filter them in the component using the nodeId
export const useOptimizedImage = (
    nodeId: string,
    fallbackAltText: string = '',
    layout: ImageLayout = 'CONSTRAINED'
): UseOptimizedImageResult => {
    const data = useStaticQuery<AllFileQuery>(graphql`
        query AllOptimizedImages {
            allFile {
                nodes {
                    id
                    childImageSharp {
                        gatsbyImageDataConstrained: gatsbyImageData(
                            formats: [AUTO, WEBP]
                            quality: 90
                            transformOptions: { fit: COVER, cropFocus: CENTER }
                            placeholder: BLURRED
                            layout: CONSTRAINED
                        )
                        gatsbyImageDataFullWidth: gatsbyImageData(
                            formats: [AUTO, WEBP]
                            quality: 90
                            transformOptions: { fit: COVER, cropFocus: CENTER }
                            placeholder: BLURRED
                            layout: FULL_WIDTH
                        )
                    }
                }
            }
        }
    `);

    const result = useMemo(() => {
        if (!nodeId) {
            return {
                rawImageData: null,
                gatsbyImageData: null,
                altText: fallbackAltText,
            };
        }

        // Find the file node by ID
        const fileNode = data.allFile.nodes.find((node: ImageNode) => node.id === nodeId);

        if (fileNode?.childImageSharp) {
            const rawImageData =
                layout === 'FULL_WIDTH'
                    ? fileNode.childImageSharp.gatsbyImageDataFullWidth
                    : fileNode.childImageSharp.gatsbyImageDataConstrained;

            const gatsbyImageData = getImage(rawImageData) ?? null;
            return {
                rawImageData,
                gatsbyImageData,
                altText: fallbackAltText,
            };
        }

        return {
            rawImageData: null,
            gatsbyImageData: null,
            altText: fallbackAltText,
        };
    }, [nodeId, fallbackAltText, layout, data]);

    return result;
};
